import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Button } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { requestTemplatePublish } from '../../api/mutations';
import { useNotifications } from '../../notifications';
import PropdocsModal from '../modal/PropdocsModal';
import { PublishRequestDate } from './PublishRequestDate';

export interface RequestPublishButtonProps {
  templateId: string;
  templateName?: string;
  publishRequestedAt?: string | null;
}

// The API sends no second email within this window of the last request, so the
// link is withheld for it rather than offering an action that does nothing.
export const PUBLISH_REQUEST_COOLDOWN_MS = 24 * 60 * 60 * 1000;

export const isPublishRequestCoolingDown = (requestedAt: string | null | undefined, now = Date.now()) => {
  if (!requestedAt) return false;
  const requestedTime = Date.parse(requestedAt);
  return !Number.isNaN(requestedTime) && now - requestedTime < PUBLISH_REQUEST_COOLDOWN_MS;
};

/**
 * The second sentence of the draft banner: an inline "ask a manager" link that
 * confirms before emailing (the click notifies other people), or — within the
 * cooldown — a plain note that the request was already sent.
 */
export const RequestPublishButton = ({ templateId, templateName, publishRequestedAt }: RequestPublishButtonProps) => {
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();
  // The mutation result also lands in the Apollo cache, but the page query is
  // network-only and the editor must not be refetched mid-edit; holding the
  // returned time locally updates the banner immediately either way.
  const [localRequestedAt, setLocalRequestedAt] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [requestPublish, { loading }] = useMutation(requestTemplatePublish);

  const requestedAt = localRequestedAt ?? publishRequestedAt ?? null;

  const sendRequest = async () => {
    try {
      const { data } = await requestPublish({ variables: { documentTemplateId: templateId } });
      setLocalRequestedAt(data?.requestTemplatePublish?.publishRequestedAt ?? new Date().toISOString());
      setConfirming(false);
      notifySuccess({ message: 'Publishing requested. Template managers have been notified by email.' });
    } catch (error) {
      notifyError({ message: (error as Error)?.message || 'Unable to request publishing. Please try again.' });
    }
  };

  if (isPublishRequestCoolingDown(requestedAt) && requestedAt) {
    return (
      <span className="template-draft-banner__requested" data-testid="publish-requested-note">
        <FormattedMessage
          id="template.request-publish.requested-note"
          defaultMessage="Publishing requested {date} — template managers have been notified."
          values={{ date: <PublishRequestDate value={requestedAt} /> }}
        />
      </span>
    );
  }

  return (
    <>
      <button type="button" className="template-draft-banner__link" onClick={() => setConfirming(true)}>
        <FormattedMessage id="template.request-publish.link" defaultMessage="Ask a template manager to publish them." />
      </button>
      <PropdocsModal
        show={confirming}
        setShow={() => !loading && setConfirming(false)}
        size="sm"
        title={<FormattedMessage id="template.request-publish.title" defaultMessage="Request publishing?" />}
      >
        <>
          <p>
            {templateName ? (
              <FormattedMessage
                id="template.request-publish.body-named"
                defaultMessage="We'll email this team's template managers that {name} is ready to review and publish."
                values={{ name: <strong>{templateName}</strong> }}
              />
            ) : (
              <FormattedMessage
                id="template.request-publish.body"
                defaultMessage="We'll email this team's template managers that this template is ready to review and publish."
              />
            )}
          </p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingTop: '1rem' }}>
            <Button variant="outline-secondary" onClick={() => setConfirming(false)} disabled={loading}>
              <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
            </Button>
            <Button variant="primary" onClick={sendRequest} disabled={loading}>
              {loading ? (
                <FormattedMessage id="template.request-publish.sending" defaultMessage="Sending..." />
              ) : (
                <FormattedMessage id="template.request-publish.send" defaultMessage="Send request" />
              )}
            </Button>
          </div>
        </>
      </PropdocsModal>
    </>
  );
};
